import type { HTMLAttributes } from "react"

import { cx } from "@uui/utils/cx"

/*
 * Status, the way the system says it: a dot and a mono word.
 *
 *   confirmed  do.500 — ticks, ready, done. Confirmed is do, never teal.
 *   caution    caution.500 — the only status hue; needs a person.
 *   system     ink @52% — sync rows, "system" as owner, settled negatives.
 *   remote     print.600 — fetched on view, not stored.
 *   neutral    no dot; the word alone.
 *
 * There is no red. An error is caution grammar.
 *
 * Inside a sentence the dot hugs its word (4px) and the unit stands off the
 * prose by a word space plus 4px, so the dot binds to its word and not to
 * the word before it.
 */
export type StatusTier =
  | "confirmed"
  | "caution"
  | "system"
  | "remote"
  | "neutral"

const DOT: Record<StatusTier, string | null> = {
  confirmed: "confirmed-dot",
  caution: "caution-dot",
  system: "system-dot",
  remote: "remote-dot",
  neutral: null,
}

const TEXT: Record<StatusTier, string> = {
  confirmed: "text-secondary",
  caution: "text-caution-700",
  system: "text-quaternary",
  remote: "text-secondary",
  neutral: "text-tertiary",
}

export function StatusDot({
  tier,
  children,
  className,
  ...props
}: { tier: StatusTier } & HTMLAttributes<HTMLSpanElement>) {
  const dot = DOT[tier]
  return (
    <span
      className={cx(
        "text-2xs tracking-chip mx-1 inline-flex items-center gap-1 font-mono leading-none font-medium",
        TEXT[tier],
        className,
      )}
      {...props}
    >
      {dot && <span aria-hidden="true" className={dot} />}
      {children}
    </span>
  )
}
